/**
 * TripMap Component - Enhanced map visualization for trips
 * Shows route line and drop markers with status colors
 */

import React, { useState, useEffect, useMemo } from 'react';
import { RouteMapWrapper } from '../../../components/RouteMap';
import { Box, Typography } from '@mui/material';
import styled from 'styled-components';
import { logError, logInfo } from '../../../shared/utils/logger.js';

const MapContainer = styled(Box)`
  height: 100%;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
  background-color: #f5f5f5;
`;

const LoadingContainer = styled(Box)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
`;

const ErrorContainer = styled(Box)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

/**
 * Get marker color based on drop status
 * @param {string} status - Drop status (pending, arrived, completed)
 * @returns {string} Color name for marker icon
 */
const getStatusColor = (status) => {
  const colorMap = {
    pending: 'yellow',    // 🟡 Planned (loaded, not yet dropped)
    arrived: 'blue',      // 🔵 Next drop / Arrived
    completed: 'green',   // 🟢 Delivered
    problem: 'red',        // 🔴 Problem / delayed
  };
  return colorMap[status] || 'grey';
};

/**
 * Geocode an address using Nominatim
 * @param {string} address - Address to geocode
 * @returns {Promise<Array|null>} [lat, lng] or null
 */
const geocodeAddress = async (address) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
      {
        headers: {
          'User-Agent': 'Bubakii Logistics Platform',
        },
      }
    );
    const data = await response.json();
    if (data.length > 0) {
      return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
    }
    return null;
  } catch (err) {
    logError('Geocoding error', {
      address,
      error: err.message,
    });
    return null;
  }
};

/**
 * Build address string from drop location
 * @param {Object} location - Drop location object
 * @returns {string} Formatted address
 */
const buildAddress = (location) => {
  if (!location) return '';
  
  const parts = [];
  if (location.address) parts.push(location.address);
  if (location.city) parts.push(location.city);
  if (location.province) parts.push(location.province);
  
  return parts.join(', ') || 'Unknown location';
};

/**
 * Calculate route between multiple waypoints using OSRM
 * @param {Array} waypoints - Array of [lat, lng] coordinates
 * @returns {Promise<Array|null>} Route coordinates or null
 */
const calculateRoute = async (waypoints) => {
  if (waypoints.length < 2) return null;
  
  try {
    // Build OSRM waypoint string (lon,lat format)
    const waypointString = waypoints.map(wp => `${wp[1]},${wp[0]}`).join(';');
    
    const response = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${waypointString}?overview=full&geometries=geojson`
    );
    const data = await response.json();
    
    if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
      // Convert GeoJSON coordinates [lon, lat] to Leaflet format [lat, lon]
      return data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
    }
    
    return null;
  } catch (err) {
    logError('Route calculation error', {
      waypointsCount: waypoints.length,
      error: err.message,
    });
    return null;
  }
};

/**
 * TripMap Component
 * @param {Object} props
 * @param {Object} props.trip - Trip object with drops array
 * @param {Array} props.drops - Array of drop objects (if trip not provided)
 * @param {Array} props.orders - Array of order objects (for popup details)
 * @param {Object} props.filters - Filter options { status, province, showCompleted }
 * @param {Function} props.onDropClick - Callback when drop marker is clicked
 * @param {boolean} props.drawerExpanded - Whether drawer is expanded
 * @param {boolean} props.showRoute - Whether to show route line (default: true)
 */
const TripMap = ({ 
  trip,
  drops = [],
  orders = [],
  filters = {},
  onDropClick,
  drawerExpanded = false,
  showRoute = true,
}) => {
  const [markers, setMarkers] = useState([]);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get drops from trip or props
  const tripDrops = useMemo(() => {
    const allDrops = drops.length > 0 ? drops : (trip?.drops || []);
    
    // Sort by sequence
    return [...allDrops].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  }, [trip, drops]);

  // Apply filters
  const filteredDrops = useMemo(() => {
    let filtered = tripDrops;
    
    if (filters.status) {
      filtered = filtered.filter(drop => drop.status === filters.status);
    }
    
    if (filters.province) {
      filtered = filtered.filter(drop => 
        drop.location?.province?.toLowerCase() === filters.province.toLowerCase()
      );
    }
    
    if (filters.showCompleted === false) {
      filtered = filtered.filter(drop => drop.status !== 'completed');
    }
    
    return filtered;
  }, [tripDrops, filters]);

  useEffect(() => {
    const loadMapData = async () => {
      if (filteredDrops.length === 0) {
        setLoading(false);
        setError('No drops to display');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Geocode all drop locations
        const geocodePromises = filteredDrops.map(async (drop) => {
          const address = buildAddress(drop.location);
          
          // Use existing coordinates if available
          if (drop.location?.coordinates?.lat && drop.location?.coordinates?.lng) {
            return {
              drop,
              coords: [drop.location.coordinates.lat, drop.location.coordinates.lng],
              address,
            };
          }
          
          // Otherwise geocode
          const coords = await geocodeAddress(address);
          return {
            drop,
            coords,
            address,
          };
        });

        const geocodedDrops = await Promise.all(geocodePromises);
        
        // Filter out drops that couldn't be geocoded
        const validDrops = geocodedDrops.filter(item => item.coords);
        
        if (validDrops.length === 0) {
          setError('Could not find coordinates for any drops');
          setLoading(false);
          return;
        }

        // Create markers
        const markerData = validDrops.map((item, index) => {
          const { drop, coords, address } = item;
          
          // Get orders for this drop
          const dropOrders = drop.orders 
            ? drop.orders.map(dropOrder => 
                orders.find(o => o.id === dropOrder.orderId)
              ).filter(Boolean)
            : [];
          
          // Build popup content
          const popupContent = (
            <Box>
              <Typography variant="subtitle2" component="strong" sx={{ display: 'block', mb: 0.5 }}>
                Drop {drop.sequence || index + 1}: {drop.location?.city || 'Unknown'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {address}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                Status: {drop.status?.toUpperCase() || 'PENDING'}
              </Typography>
              {dropOrders.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Orders: {dropOrders.length}
                </Typography>
              )}
            </Box>
          );

          return {
            coords,
            icon: getStatusColor(drop.status),
            label: `Drop ${drop.sequence || index + 1}`,
            popup: popupContent,
            drop, // Store original drop for onClick handler
          };
        });

        setMarkers(markerData);

        // Calculate route if we have multiple drops and showRoute is true
        if (showRoute && validDrops.length >= 2) {
          const waypoints = validDrops.map(item => item.coords);
          const routeCoords = await calculateRoute(waypoints);
          setRoute(routeCoords);
        }

        logInfo('TripMap loaded successfully', {
          dropsCount: validDrops.length,
          routeCalculated: !!route,
        });
      } catch (err) {
        logError('Failed to load trip map', {
          error: err.message,
          dropsCount: filteredDrops.length,
        });
        setError('Error loading map');
      } finally {
        setLoading(false);
      }
    };

    loadMapData();
  }, [filteredDrops, orders, showRoute]);

  const handleMarkerClick = (marker, index) => {
    if (onDropClick && marker.drop) {
      onDropClick(marker.drop, index);
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Typography variant="body1">Loading map...</Typography>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
        {filteredDrops.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No drops match the current filters
          </Typography>
        )}
      </ErrorContainer>
    );
  }

  if (markers.length === 0) {
    return (
      <ErrorContainer>
        <Typography variant="body1" color="text.secondary">
          No drops to display
        </Typography>
      </ErrorContainer>
    );
  }

  return (
    <MapContainer>
      <RouteMapWrapper
        markers={markers}
        route={route}
        drawerExpanded={drawerExpanded}
        autoFitBounds={true}
        onMarkerClick={handleMarkerClick}
      />
    </MapContainer>
  );
};

export default TripMap;

